import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Panel, Pill } from "@/components/admin/AdminUI";
import CountryPicker from "@/components/admin/CountryPicker";
import { api } from "@/lib/api";
import { countryName } from "@/lib/countries";

const TONE = { on: "success", waitlist: "warning", off: "neutral", blocked: "inverse" };
const MODES = [["typed", "Typed postal code"], ["device", "Device location"]];
const LEVEL = { zip: "postal code", city: "city", state: "state/province", country: "country", default: "no rule (default Off)", sanctioned: "sanctions" };

// "Typed" sends stated_zip / stated_country / stated_city (what the app sends when someone types a code).
// "Device" sends location {zip, city, state, country} (what the app sends from the phone's location).
function bodyOf(mode, f) {
  const t = (v) => v.trim() || null;
  return mode === "typed" ? { stated_zip: t(f.zip), stated_country: f.country || null, stated_city: t(f.city) }
    : { location: { zip: t(f.zip), city: t(f.city), state: t(f.state), country: f.country || null } };
}

function Result({ res }) {
  const loc = res.location || {};
  return (
    <div className="mt-4 space-y-1 text-sm" data-testid="eligibility-test-result">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={TONE[res.status]} className="capitalize" data-testid="eligibility-test-status">{res.sanctioned ? "Hard-blocked" : res.status}</Pill>
        <span className="cw-soft">matched by {LEVEL[res.level] || res.level}{res.region_label ? ` · ${res.region_label}` : ""}</span>
      </div>
      <p className="cw-muted text-xs" data-testid="eligibility-test-location">Read as: {[loc.zip, loc.city, loc.state, loc.country && countryName(loc.country)].filter(Boolean).join(" · ") || "nothing"}</p>
    </div>
  );
}

export default function EligibilityTester() {
  const [mode, setMode] = useState("typed");
  const [f, setF] = useState({ zip: "", city: "", state: "", country: "" });
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const ready = mode === "typed" ? !!f.zip.trim() : Object.values(f).some((v) => v.trim());

  async function run(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { setRes(await api("/admin/eligibility/test", { body: bodyOf(mode, f) })); } catch (err) { setError(err.message); setRes(null); } finally { setBusy(false); }
  }

  return (
    <Panel className="p-6" data-testid="eligibility-tester">
      <h2 className="font-semibold">Check a place</h2>
      <p className="cw-soft mt-1 text-sm">See what someone there would get today.</p>
      <div className="mt-4 inline-flex rounded-full bg-[var(--cw-surface-tertiary)] p-1" role="group" aria-label="What the app knows">
        {MODES.map(([k, l]) => <button key={k} type="button" onClick={() => { setMode(k); setRes(null); }} aria-pressed={mode === k}
          className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${mode === k ? "bg-white shadow-sm" : "cw-soft hover:text-foreground"}`} data-testid={`eligibility-test-mode-${k}`}>{l}</button>)}
      </div>
      <form onSubmit={run} className="mt-4 grid grid-cols-2 gap-3">
        <div><Label htmlFor="et-zip" className="text-xs">Postal code{mode === "typed" ? "" : " (optional)"}</Label>
          <Input id="et-zip" value={f.zip} onChange={set("zip")} placeholder="K1A 0B6" className="mt-1" data-testid="eligibility-test-zip-input" /></div>
        <div><Label htmlFor="et-country" className="text-xs">Country</Label>
          <CountryPicker id="et-country" value={f.country} onChange={set("country")} anyLabel="Not known" className="mt-1" testId="eligibility-test-country-picker" /></div>
        <div><Label htmlFor="et-city" className="text-xs">City</Label>
          <Input id="et-city" value={f.city} onChange={set("city")} placeholder="Ottawa" className="mt-1" data-testid="eligibility-test-city-input" /></div>
        {mode === "device" && <div><Label htmlFor="et-state" className="text-xs">State / province</Label>
          <Input id="et-state" value={f.state} onChange={set("state")} placeholder="Ontario" className="mt-1" data-testid="eligibility-test-state-input" /></div>}
        <Button type="submit" variant="outline" disabled={busy || !ready} className="col-span-2" data-testid="eligibility-test-button">{busy ? "Checking…" : "Check"}</Button>
      </form>
      {error && <p className="cw-error mt-3 text-sm" role="alert" data-testid="eligibility-test-error">{error}</p>}
      {res && <Result res={res} />}
    </Panel>
  );
}
